import { useFormikContext } from 'formik';
import React, { useEffect, useMemo } from 'react';
import {
  DialogContent,
  FormControlLabel,
  Checkbox,
  makeStyles
} from '@material-ui/core';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { DateField } from '~/modules/common/components';
import { mapRepliconDateToUtcObject } from '~/modules/common/dates/convert';
import AllocationByPeriod from '../AllocationByPeriod/AllocationByPeriod';
import useSetResourceAllocationPeriodError from './hooks/useSetResourceAllocationPeriodError';
import ClearAllocationCheckboxTooltip from './components/ClearAllocationCheckboxTooltip';
import { useSetResourceAllocationOnChangeHandlers } from './hooks/useSetResourceAllocationOnChangeHandlers';

const useStyles = makeStyles(theme => ({
  checkboxContainer: {
    display: 'flex',
    alignItems: 'center',
    marginTop: theme.spacing(1),
    columnGap: theme.spacing(1)
  },
  checkboxLabel: {
    margin: theme.spacing(0)
  },
  dateFieldContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1)
  },
  root: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2)
  }
}));

const SetResourceAllocationDialogContent = ({
  setIsFormDirty,
  setIsLoading,
  totalScheduledHours,
  setTotalScheduledHours,
  user
}) => {
  const userUri = user?.id;
  const classes = useStyles();
  const { formatMessage } = useIntl();

  const {
    dirty,
    values: {
      startDate,
      endDate,
      isClearAllocationChecked,
      selectedAllocationPeriod,
      hoursForEntirePeriod,
      percentageForEntirePeriod,
      hoursPerMonth,
      hoursPerWeek
    }
  } = useFormikContext();

  const { allocationError } = useSetResourceAllocationPeriodError();

  const {
    onStartDateChange,
    onEndDateChange,
    onClearAllocationChange,
    onSelectedAllocationPeriodChange,
    onHoursValueChange,
    onPercentageValueChange
  } = useSetResourceAllocationOnChangeHandlers({
    totalScheduledHours,
    setTotalScheduledHours,
    setIsLoading,
    userUri
  });

  const clearAllocationCheckbox = useMemo(
    () => (
      <Checkbox
        checked={isClearAllocationChecked}
        color="primary"
        onChange={onClearAllocationChange}
      />
    ),
    [isClearAllocationChecked, onClearAllocationChange]
  );

  const dateRange = useMemo(
    () => ({
      startDate: mapRepliconDateToUtcObject(startDate),
      endDate: mapRepliconDateToUtcObject(endDate)
    }),
    [endDate, startDate]
  );

  useEffect(() => {
    setIsFormDirty(dirty);
  }, [dirty, setIsFormDirty]);

  return (
    <DialogContent className={classes.root}>
      <div className={classes.dateFieldContainer}>
        <DateField
          ariaLabel={formatMessage({
            id: 'setResourceAllocationDialog.startDate'
          })}
          clearable={false}
          dataQeId="SetResourceAllocationDialog_StartDate"
          editable
          fullWidth
          onChange={onStartDateChange}
          label={formatMessage({
            id: 'setResourceAllocationDialog.startDate'
          })}
          value={startDate}
          variant="filled"
          useV5DatePicker
        />
        <DateField
          ariaLabel={formatMessage({
            id: 'setResourceAllocationDialog.endDate'
          })}
          clearable={false}
          dataQeId="SetResourceAllocationDialog_EndDate"
          editable
          fullWidth
          label={formatMessage({
            id: 'setResourceAllocationDialog.endDate'
          })}
          onChange={onEndDateChange}
          value={endDate}
          variant="filled"
          useV5DatePicker
        />
      </div>
      <div className={classes.checkboxContainer}>
        <FormControlLabel
          className={classes.checkboxLabel}
          control={clearAllocationCheckbox}
          label={formatMessage({
            id: 'setResourceAllocationDialog.clearAllocationCheckboxLabel'
          })}
        />
        <ClearAllocationCheckboxTooltip />
      </div>
      <AllocationByPeriod
        dateRange={dateRange}
        selectedAllocationPeriod={selectedAllocationPeriod}
        hoursForEntirePeriod={hoursForEntirePeriod}
        percentageForEntirePeriod={percentageForEntirePeriod}
        hoursPerMonth={hoursPerMonth}
        hoursPerWeek={hoursPerWeek}
        onHoursValueChange={onHoursValueChange}
        onPercentageValueChange={onPercentageValueChange}
        onSelectedAllocationPeriodChange={onSelectedAllocationPeriodChange}
        error={allocationError}
      />
    </DialogContent>
  );
};

SetResourceAllocationDialogContent.propTypes = {
  setIsFormDirty: PropTypes.func.isRequired,
  setIsLoading: PropTypes.func.isRequired,
  totalScheduledHours: PropTypes.number,
  setTotalScheduledHours: PropTypes.func.isRequired,
  user: PropTypes.object
};

export default SetResourceAllocationDialogContent;
